import { BlankSlate } from "@msuite/ui/blank-slate";
import { ContentBox } from "@msuite/ui/content-box";
import { icon } from "@msuite/ui/icons";
import { LoadingScreen } from "@msuite/ui/loading-screen";
import { useTranslation } from "@msuite/ui/localization";
import { Tabs } from "@msuite/ui/tabs";
import { ResponseError } from "@msuite/utils/errors";
import { useParams } from "react-router";
import { useOrder } from "@/hooks/use-order";
import { OrderHeader } from "./order-header";
import { OrderMenu } from "./order-menu";
import { OrderGeneral } from "./panels/order-general";
import { OrderProtocol } from "./panels/order-protocol";
import { OrderSuborders } from "./panels/order-suborders";

/**
 * One order: what was ordered out of stock for a site, and the zones it was split across.
 *
 * Every panel reads the order through `read Stock`, which reaches the whole warehouse or none of
 * it — so a refusal is the page rather than one card of it, and the guard below draws the locked
 * state once for all of them. A read that failed for any other reason is a different message: an
 * order that is not there is not an order somebody is being kept out of.
 */
export function Order() {
	/** Hooks */
	const { t } = useTranslation();
	const orderId = useParams().orderId as string;

	/** Query */
	const { order, isLoading, isError, error } = useOrder(orderId);

	/** Computed */
	const isForbidden = error instanceof ResponseError && error.status === 403;

	/** Guard */
	if (isForbidden) {
		return (
			<ContentBox>
				<BlankSlate
					Icon={icon("Lock")}
					label={t("general.no_access")}
					withAccessory={false}
				/>
			</ContentBox>
		);
	}
	if (isError) {
		return (
			<ContentBox>
				<BlankSlate label={t("stock.order_not_loaded")} />
			</ContentBox>
		);
	}
	if (isLoading) return <LoadingScreen />;
	if (!order) {
		return (
			<ContentBox>
				<BlankSlate label={t("stock.order_not_loaded")} />
			</ContentBox>
		);
	}

	/** Render */
	return (
		<ContentBox bottomPadding="none">
			<OrderHeader orderId={orderId} />
			<Tabs
				header={() => <OrderMenu orderId={orderId} />}
				groups={[
					{
						name: "general",
						panels: [
							{
								label: t("general.general"),
								cyId: "order-general-tab",
								panel: () => <OrderGeneral orderId={orderId} />,
								errors: [],
							},
						],
					},
					{
						name: "information",
						panels: [
							{
								label: t("stock.suborders"),
								cyId: "order-suborders-tab",
								panel: () => <OrderSuborders orderId={orderId} />,
								errors: [],
							},
							{
								label: t("stock.protocol"),
								cyId: "order-protocol-tab",
								panel: () => <OrderProtocol orderId={orderId} />,
								errors: [],
							},
						],
					},
				]}
			/>
		</ContentBox>
	);
}
